import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { COUNTRY_CODES } from '@/config/countryCodes';
import styles from '../AuthPage.module.css';

/**
 * CountryCodeSelect — searchable phone country code picker.
 *
 * Mirrors Flutter CustomDialog.showCountryCodeBottomSheet (CustomCountryListView):
 * "Name +code" rows, filtered by name or code as the user types.
 */

interface CountryCodeSelectProps {
  value: string;
  onChange: (phoneCode: string) => void;
  disabled?: boolean;
}

export function CountryCodeSelect({ value, onChange, disabled }: CountryCodeSelectProps) {
  const { t } = useLocalization();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^\+/, '');
    if (!q) return COUNTRY_CODES;
    const matches = COUNTRY_CODES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.phoneCode.startsWith(q) || c.iso2.toLowerCase() === q,
    );
    // Names starting with the query first ("mala" → Malaysia before Guatemala)
    const rank = (name: string) => (name.toLowerCase().startsWith(q) ? 0 : 1);
    return matches.sort((a, b) => rank(a.name) - rank(b.name));
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function select(phoneCode: string) {
    onChange(phoneCode);
    setOpen(false);
    setQuery('');
  }

  return (
    <div className={styles.countryCode} ref={rootRef}>
      <button
        type="button"
        className={styles.countryCodeBtn}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('Country code')}
      >
        +{value}
        <span className={styles.countryCodeCaret} aria-hidden>▾</span>
      </button>

      {open && (
        <div className={styles.countryMenu}>
          <input
            type="text"
            className={styles.input}
            placeholder={t('Start typing to search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (filtered[0]) select(filtered[0].phoneCode);
              }
            }}
            autoFocus
            aria-label={t('Search')}
          />
          <ul className={styles.countryList} role="listbox">
            {filtered.map((c) => (
              <li key={c.iso2}>
                <button
                  type="button"
                  role="option"
                  aria-selected={c.phoneCode === value}
                  className={`${styles.countryOption} ${c.phoneCode === value ? styles.countryOptionActive : ''}`}
                  onClick={() => select(c.phoneCode)}
                >
                  <span>{c.name}</span>
                  <span className={styles.countryOptionCode}>+{c.phoneCode}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className={styles.countryEmpty}>{t('No results')}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
